/**
 * Utility functions for managing the ICD-10 database via server API
 */

// API base URL - using relative URL for production/development compatibility
const API_BASE_URL = '';

// IndexedDB configuration
const DB_NAME = 'IcdDatabase';
const DB_VERSION = 1;
const STORE_NAME = 'icd_codes';
let dbPromise = null;

/**
 * Initialize the ICD database connection
 * @returns {Promise<boolean>} True if the database was initialized successfully
 */
export const initializeDatabase = async () => {
  if (dbPromise) {
    return dbPromise;
  }

  console.log('Initializing ICD database in IndexedDB');
  
  dbPromise = new Promise((resolve, reject) => {
    // Check if IndexedDB is supported
    if (!window.indexedDB) {
      console.error('IndexedDB not supported by this browser');
      reject(new Error('IndexedDB not supported by this browser'));
      return;
    }
    
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error('Error opening IndexedDB:', event.target.error);
      reject(event.target.error);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      console.log('Creating or upgrading ICD database structure');
      
      // Create object store with code as key path
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'code' });
        // Create index for text search
        store.createIndex('text', 'text', { unique: false });
        console.log('Object store created');
      }
    };
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      console.log('IndexedDB connection established');
      resolve(db);
    };
  });
  
  try {
    await dbPromise;
    console.log('ICD database initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize ICD database:', error);
    dbPromise = null;
    return false;
  }
};

/**
 * Check if the ICD database is available and has data
 * @returns {Promise<boolean>} True if the database exists and has data
 */
export const isDatabaseAvailable = async () => {
  try {
    // First check if we have any data in IndexedDB
    const count = await getRecordCount();
    if (count > 0) {
      console.log(`ICD database available with ${count} records in browser IndexedDB`);
      return true;
    }
    
    // If no data in IndexedDB, check with server
    const response = await fetch(`${API_BASE_URL}/api/icd/status`);
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.available;
  } catch (error) {
    console.error('Error checking ICD database availability:', error);
    return false;
  }
};

/**
 * Get the count of records in the IndexedDB database
 * @returns {Promise<number>} Count of records
 */
const getRecordCount = async () => {
  try {
    await initializeDatabase();
    const db = await dbPromise;
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      // Count operation
      const countRequest = store.count();
      
      countRequest.onsuccess = () => {
        resolve(countRequest.result);
      };
      
      countRequest.onerror = (event) => {
        reject(event.target.error);
      };
      
      transaction.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error getting record count:', error);
    return 0;
  }
};

/**
 * Get database statistics
 * @returns {Promise<Object>} Database statistics (count, lastUpdated)
 */
export const getDatabaseStats = async () => {
  try {
    // Get local count first
    const localCount = await getRecordCount();
    
    // Get server status
    const response = await fetch(`${API_BASE_URL}/api/icd/status`);
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    const serverData = await response.json();
    
    return {
      count: localCount || serverData.count || 0,
      lastUpdated: serverData.lastUpdated,
      sourceFile: serverData.sourceFile,
      fileSize: serverData.fileSize,
      localCount: localCount
    };
  } catch (error) {
    console.error('Error getting ICD database stats:', error);
    // Try to at least return the local count if server fails
    try {
      const localCount = await getRecordCount();
      return { count: localCount, lastUpdated: null, sourceFile: null, localCount };
    } catch (e) {
      return { count: 0, lastUpdated: null, sourceFile: null, localCount: 0 };
    }
  }
};

/**
 * Load the ICD database from the server and store it in IndexedDB
 * @param {boolean} forceReload - Force reload even if data exists
 * @param {function} progressCallback - Callback for progress updates
 * @returns {Promise<boolean>} True if the database was loaded successfully
 */
export const loadIcdDatabaseFromServer = async (forceReload = false, progressCallback = null) => {
  try {
    // First check if we need to load
    if (!forceReload) {
      const count = await getRecordCount();
      if (count > 0) {
        console.log(`Database already contains ${count} records, skipping load`);
        return true;
      }
    }

    console.log('Loading ICD database from server...');
    if (progressCallback) progressCallback({ status: 'starting', message: 'Starte ICD Datenbank-Download' });
    
    // Get server status to check if there's data to load
    const statusResponse = await fetch(`${API_BASE_URL}/api/icd/status`);
    if (!statusResponse.ok) {
      throw new Error('Failed to check ICD database status');
    }
    
    const statusData = await statusResponse.json();
    if (!statusData.available || statusData.count === 0) {
      console.log('No ICD data available on server');
      if (progressCallback) progressCallback({ 
        status: 'error', 
        message: 'Keine ICD Daten auf dem Server verfügbar' 
      });
      return false;
    }
    
    const totalEntries = statusData.count;
    console.log(`Server reports ${totalEntries} ICD entries available`);
    
    if (progressCallback) progressCallback({ 
      status: 'downloading', 
      message: `Lade ${totalEntries.toLocaleString()} ICD Einträge...` 
    });
    
    // Load all ICD codes in batches of 1000
    // We'll use a page parameter to get batches
    const batchSize = 1000;
    let loadedCount = 0;
    let page = 0;
    
    // First clear the database if we're reloading
    if (forceReload) {
      await clearIcdDatabase();
    }
    
    // Initialize the database
    await initializeDatabase();
    const db = await dbPromise;
    
    while (loadedCount < totalEntries) {
      if (progressCallback) {
        progressCallback({ 
          status: 'downloading', 
          message: `Lade ICD Einträge (${loadedCount}/${totalEntries})...`,
          progress: Math.round((loadedCount / totalEntries) * 100)
        });
      }
      
      // Fetch the next batch
      const response = await fetch(`${API_BASE_URL}/api/icd/batch?page=${page}&size=${batchSize}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch ICD batch: ${response.statusText}`);
      }
      
      const batch = await response.json();
      if (!Array.isArray(batch) || batch.length === 0) {
        // No more data or an error occurred
        break;
      }
      
      // Store the batch in IndexedDB
      await storeIcdBatch(db, batch);
      
      // Update counters
      loadedCount += batch.length;
      page++;
    }
    
    console.log(`Successfully loaded ${loadedCount} ICD entries into IndexedDB`);
    if (progressCallback) progressCallback({ 
      status: 'complete', 
      message: `${loadedCount.toLocaleString()} ICD Einträge erfolgreich geladen`,
      progress: 100
    });
    
    return true;
  } catch (error) {
    console.error('Error loading ICD database from server:', error);
    if (progressCallback) progressCallback({ 
      status: 'error', 
      message: `Fehler beim Laden der ICD Datenbank: ${error.message}`
    });
    return false;
  }
};

/**
 * Store a batch of ICD codes in IndexedDB
 * @param {IDBDatabase} db - IndexedDB database instance
 * @param {Array} batch - Array of ICD entries
 * @returns {Promise<void>}
 */
const storeIcdBatch = (db, batch) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    transaction.onerror = (event) => {
      reject(event.target.error);
    };
    
    transaction.oncomplete = () => {
      resolve();
    };
    
    // Add each entry to the store - normalize text to handle encoding issues
    batch.forEach(entry => {
      // Fix encoding issues with German umlauts and special characters
      if (entry.text) {
        // First try to normalize if it's UTF-8 displayed as Latin1 
        entry.text = normalizeText(entry.text);
      }
      store.put(entry);
    });
  });
};

/**
 * Normalize text to handle encoding issues with German umlauts and special characters
 * @param {string} text - The text to normalize
 * @returns {string} - Normalized text
 */
const normalizeText = (text) => {
  if (!text) return text;
  
  // Replace common encoding errors for German umlauts
  return text
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ã„/g, 'Ä')
    .replace(/Ã–/g, 'Ö')
    .replace(/Ãœ/g, 'Ü')
    .replace(/ÃŸ/g, 'ß')
    .replace(/Ã©/g, 'é')
    .replace(/Ã¨/g, 'è')
    .replace(/Ã /g, 'à')
    .replace(/Ã¢/g, 'â')
    .replace(/Ãª/g, 'ê')
    .replace(/Ã®/g, 'î')
    .replace(/Ã´/g, 'ô')
    .replace(/Ã»/g, 'û')
    .replace(/Ã§/g, 'ç')
    .replace(/â€™/g, "'") // Fixed: use standard single quote instead of curly quote
    .replace(/â€œ/g, '"') // Fixed: use standard double quote instead of curly quotes
    .replace(/â€/g, '"')  // Fixed: use standard double quote
    .replace(/â€¦/g, '...'); // Fixed: use three dots instead of ellipsis character
};

/**
 * Clear all data from the ICD database
 * @returns {Promise<void>}
 */
const clearIcdDatabase = async () => {
  try {
    await initializeDatabase();
    const db = await dbPromise;
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const clearRequest = store.clear();
      
      clearRequest.onsuccess = () => {
        console.log('ICD database cleared successfully');
        resolve();
      };
      
      clearRequest.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error clearing ICD database:', error);
    throw error;
  }
};

/**
 * Upload ICD file to server for parsing and database population
 * @param {File} file The ICD-10 text file to upload
 * @returns {Promise<Object>} Upload result
 */
export const uploadIcdFile = async (file) => {
  try {
    console.log(`Starting upload of ICD file: ${file.name} (${file.size} bytes)`);
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Use fetch with improved error handling and timeout control
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000); // 15 minute timeout
    
    try {
      // Make sure we have proper credentials
      const response = await fetch(`${API_BASE_URL}/api/icd/upload`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        credentials: 'include', // Include cookies for authorization
        // Don't set Content-Type header - browser will set it with boundary for FormData
      });
      
      clearTimeout(timeoutId);
      
      // Always read the response body text first
      let responseText;
      try {
        responseText = await response.text();
      } catch (textErr) {
        console.error('Error reading response text:', textErr);
        responseText = '';
      }
      
      // Now try to parse it as JSON if possible
      let responseData;
      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch (parseErr) {
        console.error('Error parsing response as JSON:', parseErr, 'Response was:', responseText);
        // If we couldn't parse JSON, wrap the text in an error object
        if (!response.ok) {
          throw new Error(`Server returned status ${response.status}: ${responseText || 'No response data'}`);
        }
      }
      
      // If we have JSON data, check for errors
      if (responseData) {
        if (!response.ok || responseData.error) {
          throw new Error(responseData.error || `Server error: ${response.status}`);
        }
        return responseData;
      }
      
      // If we get here, we have a non-JSON response that's not an error
      if (!response.ok) {
        throw new Error(`Server returned ${response.status} with non-JSON response`);
      }
      
      // Default return if we couldn't parse JSON but response was OK
      return { success: true, message: 'File successfully uploaded' };
      
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        throw new Error('Upload timed out. The file might be too large or the server too busy.');
      }
      
      throw fetchError;
    }
  } catch (error) {
    console.error('Error uploading ICD file:', error);
    throw error;
  }
};

/**
 * Check import status for a specific import ID
 * @param {string} importId The ID of the import process
 * @returns {Promise<Object>} Import status
 */
export const checkImportStatus = async (importId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/icd/import-status/${importId}`);
    
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || response.statusText);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking import status:', error);
    throw error;
  }
};

/**
 * Search for ICD entries in the local IndexedDB database
 * @param {string} query Search query (code or text)
 * @param {Object} options Search options
 * @returns {Promise<Array>} Array of matching ICD entries
 */
export const searchIcd = async (query, options = {}) => {
  const { limit = 100, exactMatch = false } = options;
  
  if (!query || query.trim().length === 0) {
    return [];
  }
  
  try {
    await initializeDatabase();
    const db = await dbPromise;
    const normalizedQuery = query.trim().toUpperCase();
    
    // See if we have any data in the database
    const count = await getRecordCount();
    if (count === 0) {
      console.log('No data in IndexedDB, falling back to server search');
      return searchIcdOnServer(normalizedQuery, options);
    }
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const results = [];
      
      // Handle exact match search
      if (exactMatch) {
        const getRequest = store.get(normalizedQuery);
        
        getRequest.onsuccess = () => {
          if (getRequest.result) {
            const result = getRequest.result;
            // Fix encoding issues with results
            if (result.text) {
              result.text = normalizeText(result.text);
            }
            results.push(result);
          }
          resolve(results);
        };
        
        getRequest.onerror = (event) => {
          reject(event.target.error);
        };
      } else {
        // For code prefix or text search, we need to iterate
        // First check if the query looks like an ICD code pattern
        const isCodePattern = /^[A-Z]\d{0,2}(\.\d{0,1})?$/.test(normalizedQuery);
        
        if (isCodePattern) {
          // If it looks like a code, do a range search for better performance
          const range = IDBKeyRange.bound(
            normalizedQuery, 
            normalizedQuery + '\uffff', 
            false, 
            false
          );
          
          const cursorRequest = store.openCursor(range);
          
          cursorRequest.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor && results.length < limit) {
              const result = cursor.value;
              // Fix encoding issues with results
              if (result.text) {
                result.text = normalizeText(result.text);
              }
              results.push(result);
              cursor.continue();
            } else {
              resolve(results);
            }
          };
          
          cursorRequest.onerror = (event) => {
            reject(event.target.error);
          };
        } else {
          // Text search - need to scan through all records
          // Use the text index if available
          try {
            const index = store.index('text');
            const cursorRequest = index.openCursor();
            
            cursorRequest.onsuccess = (event) => {
              const cursor = event.target.result;
              if (cursor && results.length < limit) {
                const result = cursor.value;
                let text = result.text || '';
                // Normalize text for comparison
                const normalizedText = normalizeText(text).toLowerCase();
                const normalizedQueryLower = normalizedQuery.toLowerCase();
                
                if (normalizedText.includes(normalizedQueryLower)) {
                  // Fix encoding issues with results
                  if (result.text) {
                    result.text = normalizeText(result.text);
                  }
                  results.push(result);
                }
                cursor.continue();
              } else {
                resolve(results);
              }
            };
            
            cursorRequest.onerror = (event) => {
              reject(event.target.error);
            };
          } catch (indexError) {
            // Fallback if index is not available
            const cursorRequest = store.openCursor();
            
            cursorRequest.onsuccess = (event) => {
              const cursor = event.target.result;
              if (cursor && results.length < limit) {
                const result = cursor.value;
                const code = result.code;
                let text = result.text || '';
                
                // Normalize text for comparison
                const normalizedText = normalizeText(text).toLowerCase();
                const normalizedQueryLower = normalizedQuery.toLowerCase();
                
                if (code.startsWith(normalizedQuery) || normalizedText.includes(normalizedQueryLower)) {
                  // Fix encoding issues with results
                  if (result.text) {
                    result.text = normalizeText(result.text);
                  }
                  results.push(result);
                }
                cursor.continue();
              } else {
                resolve(results);
              }
            };
            
            cursorRequest.onerror = (event) => {
              reject(event.target.error);
            };
          }
        }
      }
      
      transaction.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error searching ICD database locally:', error);
    // Fall back to server search if IndexedDB fails
    return searchIcdOnServer(query, options);
  }
};

/**
 * Fall back to server-side search if local database fails
 * @param {string} query Search query
 * @param {Object} options Search options
 * @returns {Promise<Array>} Search results
 */
const searchIcdOnServer = async (query, options = {}) => {
  const { limit = 100, exactMatch = false } = options;
  
  try {
    console.log('Falling back to server search for ICD codes');
    const params = new URLSearchParams({
      query,
      limit,
      exact: exactMatch
    });
    
    const response = await fetch(`${API_BASE_URL}/api/icd/search?${params}`);
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    const results = await response.json();
    
    // Normalize text in each result to fix encoding issues
    return results.map(result => {
      if (result.text) {
        result.text = normalizeText(result.text);
      }
      return result;
    });
  } catch (error) {
    console.error('Error searching ICD on server:', error);
    return [];
  }
};
